import { SxProps, Theme } from "@mui/material";
import { z } from "zod";
import { tableDraggingColor } from "../constants/dragAndDrop";
import {
  AssignShipmentType,
  assignShipmentTypeSchema,
} from "../types/assignShipment.type";
import type { ShipmentWithSequence } from "../types/planning/shipmentSeq.type";

// CSpell:ignore skyblue
export function getDroppableStyleRow(isDraggingOver?: boolean): SxProps {
  return {
    background: isDraggingOver ? "skyblue" : "unset",
    border: 1,
    borderRadius: 1,
    margin: 1,
    padding: 2,
    transition: "background-color 0.5s ease",
  };
}

export function getDroppableStyleCard(isDraggingOver: boolean): SxProps {
  return {
    background: isDraggingOver ? "skyblue" : "",
    margin: isDraggingOver ? 1 : undefined,
    padding: isDraggingOver ? 2 : undefined,
    transition: "background-color 0.5s ease",
  };
}

export function getDraggableStyleCard(
  isDragging: boolean,
  draggableStyle: any,
  theme: Theme,
  draggingBackgroundColor?: string,
  isSelected?: boolean
): React.CSSProperties {
  return {
    userSelect: "none",
    backgroundColor: isDragging
      ? draggingBackgroundColor
      : isSelected
        ? theme.palette.action.selected
        : "white",
    ...draggableStyle,
  };
}

export function getDraggableStyleRow(
  isDragging: boolean,
  draggableStyle: any,
  theme: Theme,
  isSelected?: boolean
): React.CSSProperties {
  return {
    userSelect: "none",
    position: !isDragging ? "absolute !important" : undefined,
    backgroundColor: isDragging
      ? tableDraggingColor
      : isSelected
        ? theme.palette.action.selected
        : "white",
    ...draggableStyle,
  };
}

const deliveryDataType = z.enum(["delivery_trap", "delivery_route"]);
type DeliveryDataType = z.infer<typeof deliveryDataType>;

type DroppableIdType = {
  source: DragSourceOrDestination;
  dataType: AssignShipmentType | DeliveryDataType;
  /** can be undefined if the destination is a trailer */
  id?: string | number;
  /**  can be undefined if the shipment is unassigned */
  parentId?: string | number;
  suffix?: string;
};

export const getDroppableId = ({
  source,
  dataType,
  id,
  parentId,
  suffix,
}: DroppableIdType) => {
  if (dataType === "delivery_route") {
    dataType = "route";
  } else if (dataType === "delivery_trap") {
    dataType = "trap";
  }

  const string = `${source}-${dataType}-${id ?? "_"}-${parentId ?? "_"}-${suffix ?? "_"}`;

  return string;
};

export const oldGetDroppableId = (
  source: DragSourceOrDestination,
  dataType: AssignShipmentType | DeliveryDataType,
  id: string | number,
  parentId?: string | number,
  suffix?: string
) => {
  if (dataType === "delivery_route") {
    dataType = "route";
  } else if (dataType === "delivery_trap") {
    dataType = "trap";
  }

  let string = `${source}-${dataType}-${id}`;
  if (parentId) string += `-${parentId}`;
  if (suffix) string += `-${suffix}`;

  return string;
};

export const dragSourceOrDestinationSchema = z.enum([
  "mainTable",
  "shipmentTable",
  "sidebar",
  "mainGrid",
  "unassigned",
]);
export type DragSourceOrDestination = z.infer<
  typeof dragSourceOrDestinationSchema
>;

const droppableIdSchema = z
  .array(z.string())
  .min(3, "droppableId must have at least three elements.")
  .max(5, "droppableId must have a maximum of five elements.")
  .superRefine((parts, ctx) => {
    if (!dragSourceOrDestinationSchema.safeParse(parts[0]).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The field 'source' is invalid against the expected schema.",
        path: [0],
      });
    }
    if (!assignShipmentTypeSchema.safeParse(parts[1]).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The field 'type' is invalid against the expected schema.",
        path: [1],
      });
    }
  });

type Droppable = {
  source: DragSourceOrDestination;
  type: AssignShipmentType;
  id?: string;
  parentId?: string;
  suffix?: string;
};

/** Accepts a droppableId and returns a structured object.
 * @param droppableId - The droppableId to parse using the droppableIdSchema
 */
export function parseDroppableId(droppableId: string): Droppable {
  const [source, type, id, parentId, suffix] = droppableIdSchema.parse(
    droppableId.split("-")
  );

  const droppable: Droppable = {
    source: dragSourceOrDestinationSchema.parse(source),
    type: assignShipmentTypeSchema.parse(type),
    id: id.replace("_", "") || undefined,
    parentId: parentId.replace("_", "") || undefined,
    suffix: suffix.replace("_", "") || undefined,
  };

  return droppable;
}

export const getAfterShipmentId = (
  shipments: ShipmentWithSequence[],
  destinationStopSeq: number,
  edge: "top" | "bottom"
) => {
  // To get the correct afterShipmentId position, we need to know if the destination is higher up or down
  // from the source stop sequence. If the destination is stop sequence 1, we use "0" to indicate
  // that it should be put at the first position

  // Header drop (before first row)
  if (destinationStopSeq === 1 && edge === "top") {
    return 0;
  }

  // Determine which stopSeq we should reference
  const targetStopSeq =
    edge === "top" ? destinationStopSeq - 1 : destinationStopSeq;

  const shipment = shipments.find((s) => s.stopSeq === targetStopSeq);

  return shipment?.id;
};
